import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Alert } from '@mui/material';
import { QrCode as QrCodeIcon, CameraAlt as CameraIcon, Close as CloseIcon } from '@mui/icons-material';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { useLanguage } from '../contexts/LanguageContext';

const ZXingBarcodeScanner = ({ open, onClose, onScan }) => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const codeReader = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [scannedCode, setScannedCode] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const hasScannedRef = useRef(false);

  const startScan = useCallback(() => {
    setError(null);
    setScannedCode(null);
    setShowResult(false);
    setScanning(true);
    hasScannedRef.current = false; // Réinitialiser le flag
    codeReader.current = new BrowserMultiFormatReader();
    
    codeReader.current.decodeFromVideoDevice(
      null,
      videoRef.current,
      (result, err) => {
        if (result && !hasScannedRef.current) {
          const code = result.getText();
          if (/^\d{13}$/.test(code)) {
            hasScannedRef.current = true; // Empêcher les scans multiples
            setScannedCode(code);
            setShowResult(true);
            
            // Arrêter immédiatement le scanner
            if (codeReader.current && typeof codeReader.current.reset === 'function') {
              codeReader.current.reset();
            }
            
            setTimeout(() => {
              setShowResult(false);
              onScan(code);
              handleClose();
            }, 1000);
          }
        } else if (err && err.name !== 'NotFoundException') {
          setError('Erreur lors du scan : ' + err);
        }
      }
    ).then(() => {
      setScanning(false);
    }).catch((e) => {
      setError("Impossible d'accéder à la caméra. Vérifiez les permissions.");
      setScanning(false);
    });
  }, [onScan]);

  useEffect(() => {
    if (open) {
      startScan();
    }
    return () => {
      if (codeReader.current && typeof codeReader.current.reset === 'function') {
        codeReader.current.reset();
      }
    };
  }, [open, startScan]);

  const restartScan = () => {
    if (codeReader.current && typeof codeReader.current.reset === 'function') {
      codeReader.current.reset();
    }
    startScan();
  };

  const handleManualInput = () => {
    const barcode = prompt('Entrez le code-barres manuellement:');
    if (barcode && /^\d{13}$/.test(barcode.trim())) {
      onScan(barcode.trim());
      handleClose();
    } else {
      setError('Veuillez entrer un code-barres valide (13 chiffres).');
    }
  };

  const handleClose = useCallback(() => {
    if (codeReader.current && typeof codeReader.current.reset === 'function') {
      codeReader.current.reset();
    }
    hasScannedRef.current = false; // Réinitialiser le flag
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{t('scan.scanBarcode') || 'Scanner un code-barres'}</Typography>
          <Button onClick={handleClose} size="small"><CloseIcon /></Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {showResult && scannedCode && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {t('scan.codeDetected') || 'Code détecté'} : <b>{scannedCode}</b>
          </Alert>
        )}
        <Box
          sx={{
            width: '100%',
            height: 300,
            backgroundColor: '#000',
            borderRadius: 1,
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <video ref={videoRef} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center' }}>
          {t('scan.pointCamera') || 'Pointez la caméra vers un code-barres (13 chiffres) pour le scanner automatiquement.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleManualInput} startIcon={<QrCodeIcon />} variant="outlined">
          {t('scan.manualInput') || 'Saisie manuelle'}
        </Button>
        <Button onClick={restartScan} startIcon={<CameraIcon />} variant="contained" disabled={scanning}>
          {scanning ? (t('scan.starting') || 'Démarrage...') : (t('scan.restartCamera') || 'Redémarrer la caméra')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ZXingBarcodeScanner; 